/**
 * On-device STILL quality gate — forked from vCIP_droom_4.0/src/media/imageQuality.ts (S7-6;
 * UX_SPEC §3.4 safety condition 4) with the maths moved to capture/frameMetrics.ts and the floors to
 * capture/thresholds.ts (one place, PROVISIONAL, `Rule-based`).
 *
 * Assesses the ACTUAL JPEG that would be uploaded on a ~256 px grayscale downscale. A failed gate
 * BLOCKS the capture with a named reason in calm copy — never advances, never uploads, never
 * silently passes. An unanalysable frame is a BLOCKED verdict (fail closed). This is a UX gate,
 * not evidence: the server's engines judge quality again.
 */

import { frameMetrics } from '@/capture/frameMetrics';
import { GATE_VERSION, LUMA_MAX, LUMA_MIN, STILL_GATES, type StillKind } from '@/capture/thresholds';

export type QualityReason = 'TOO_SMALL' | 'TOO_DARK' | 'TOO_BRIGHT' | 'GLARE' | 'TOO_BLURRY' | 'UNREADABLE';

export interface QualityMetrics {
  readonly width: number;
  readonly height: number;
  readonly shortSide: number;
  readonly meanLuma: number;
  readonly laplacianVar: number;
  /** Clipped-highlight ratio (frameMetrics); optional so pinned metric literals without it still judge. */
  readonly clippedRatio?: number;
}

export interface QualityVerdict {
  readonly ok: boolean;
  readonly reason: QualityReason | null;
  readonly messageKey: string | null;
  readonly metrics: QualityMetrics;
  readonly thresholds: {
    readonly minShortSide: number;
    readonly minLaplacianVar: number;
    readonly lumaMin: number;
    readonly lumaMax: number;
    readonly maxClippedRatio: number | null;
    readonly version: string;
  };
}

const ANALYSIS_WIDTH = 256;

/** The calm, named reason per kind — the document variants say what to do with the CARD, not the room. */
function messageKeyFor(reason: QualityReason, kind: StillKind): string {
  const doc = kind === 'SV_ID_DOC';
  switch (reason) {
    case 'TOO_SMALL':
      return doc ? 'sv.gate.still.small.doc' : 'sv.gate.still.small';
    case 'TOO_DARK':
      return doc ? 'sv.gate.still.dark.doc' : 'sv.gate.still.dark';
    case 'TOO_BRIGHT':
      return doc ? 'sv.gate.still.glare.doc' : 'sv.gate.still.bright';
    case 'GLARE':
      return 'sv.gate.still.glare.doc';
    case 'TOO_BLURRY':
      return doc ? 'sv.gate.still.blurry.doc' : 'sv.gate.still.blurry';
    case 'UNREADABLE':
      return 'sv.gate.still.unreadable';
  }
}

function thresholdsFor(kind: StillKind): QualityVerdict['thresholds'] {
  const t = STILL_GATES[kind];
  return { minShortSide: t.minShortSide, minLaplacianVar: t.minLaplacianVar, lumaMin: LUMA_MIN, lumaMax: LUMA_MAX, maxClippedRatio: t.maxClippedRatio ?? null, version: GATE_VERSION };
}

function blocked(reason: QualityReason, kind: StillKind, metrics: QualityMetrics): QualityVerdict {
  return { ok: false, reason, messageKey: messageKeyFor(reason, kind), metrics, thresholds: thresholdsFor(kind) };
}

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}

async function decode(blob: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await createImageBitmap(blob);
    return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode failed'));
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/** Pure verdict from already-measured metrics — pinned by tests without a canvas. */
export function judgeStill(metrics: QualityMetrics, kind: StillKind): QualityVerdict {
  const t = STILL_GATES[kind];
  if (metrics.shortSide < t.minShortSide) return blocked('TOO_SMALL', kind, metrics);
  if (metrics.meanLuma < LUMA_MIN) return blocked('TOO_DARK', kind, metrics);
  if (metrics.meanLuma > LUMA_MAX) return blocked('TOO_BRIGHT', kind, metrics);
  // Glare (a clipped patch) is judged before sharpness: a reflection band also flattens edges, and "move out of
  // the light" is the fix the representative can act on; "hold steady" would send them chasing the wrong cause.
  if (t.maxClippedRatio !== undefined && typeof metrics.clippedRatio === 'number' && metrics.clippedRatio > t.maxClippedRatio) {
    return blocked('GLARE', kind, metrics);
  }
  if (metrics.laplacianVar < t.minLaplacianVar) return blocked('TOO_BLURRY', kind, metrics);
  return { ok: true, reason: null, messageKey: null, metrics, thresholds: thresholdsFor(kind) };
}

/** Run the gate on a captured frame. Never throws — an unanalysable frame is BLOCKED (fail closed). */
export async function assessCaptureQuality(blob: Blob, kind: StillKind): Promise<QualityVerdict> {
  const zero: QualityMetrics = { width: 0, height: 0, shortSide: 0, meanLuma: 0, laplacianVar: 0, clippedRatio: 0 };
  let decoded: DecodedImage;
  try {
    decoded = await decode(blob);
  } catch {
    return blocked('UNREADABLE', kind, zero);
  }
  try {
    const { width, height } = decoded;
    if (width < 2 || height < 2) return blocked('UNREADABLE', kind, zero);
    const shortSide = Math.min(width, height);
    const scale = Math.min(1, ANALYSIS_WIDTH / width);
    const aw = Math.max(2, Math.round(width * scale));
    const ah = Math.max(2, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = aw;
    canvas.height = ah;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return blocked('UNREADABLE', kind, zero);
    ctx.drawImage(decoded.source, 0, 0, aw, ah);
    const { meanLuma, laplacianVar, clippedRatio } = frameMetrics(ctx.getImageData(0, 0, aw, ah).data, aw, ah);
    return judgeStill({ width, height, shortSide, meanLuma, laplacianVar, clippedRatio }, kind);
  } catch {
    return blocked('UNREADABLE', kind, zero);
  } finally {
    decoded.close();
  }
}
